
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type OnboardingStatus = 'onboarded' | 'invited_pending' | 'invited_expired' | 'not_invited';

export interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  country?: string;
  contact_type: 'Buyer' | 'Influencer' | 'Industry Expert';
  relationship_status: 'Prospect' | 'Customer';
  customer_since?: string;
  status: string;
  user_status?: 'no_account' | 'invitation_sent' | 'active' | 'inactive';
  profile_image?: string;
  organization_id?: string;
  internal_owner_id?: string;
  external_owner_id?: string;
  notes?: string;
  intended_system_role?: 'standard' | 'admin';
  created_at: string;
  updated_at: string;
  // Archive fields
  archived?: boolean;
  archived_at?: string;
  archived_reason?: string;
  // Onboarding status
  onboarding_status?: OnboardingStatus;
  // Last seen
  last_seen_at?: string;
  // Partner ownership approval
  partner_ownership_status?: 'pending' | 'approved' | 'rejected' | null;
  organizations?: {
    id: string;
    name: string;
    company_id?: string;
    relationship_status?: string;
  };
  internal_owner?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  external_owner?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  linked_user?: {
    id: string;
    username: string;
    email: string;
    organization_name?: string;
    role?: string;
  };
  has_user_account?: boolean;
  username?: string;
}

export interface CreateContactData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  country?: string;
  contact_type: 'Buyer' | 'Influencer' | 'Industry Expert';
  relationship_status?: 'Prospect' | 'Customer';
  customer_since?: string;
  status?: string;
  user_status?: 'no_account' | 'invitation_sent' | 'active' | 'inactive';
  profile_image?: string;
  organization_id?: string;
  internal_owner_id?: string;
  external_owner_id?: string;
  user_id?: string;
  notes?: string;
  intended_system_role?: 'standard' | 'admin';
  partner_ownership_status?: 'pending' | 'approved' | 'rejected';
}

export const useContacts = (showArchived: boolean = false) => {
  return useQuery({
    queryKey: ["contacts", showArchived],
    queryFn: async () => {
      console.log("Fetching contacts with user role information... showArchived:", showArchived);
      
      let query = supabase
        .from('profiles')
        .select(`
          *,
          organizations!fk_profiles_organization(id, name, company_id, relationship_status),
          internal_owner:crm_staff!fk_profiles_internal_owner(id, first_name, last_name),
          external_owner:crm_partners!fk_profiles_external_owner(id, first_name, last_name)
        `)
        .not('first_name', 'is', null) // Only get profiles that have CRM data
        .not('last_name', 'is', null)
        .neq('contact_type', 'Partner'); // Exclude partner profiles from contacts

    // Filter by archived status
    if (showArchived) {
      // When toggle is ON, show ONLY archived contacts
      query = query.eq('archived', true);
    } else {
      // When toggle is OFF, show non-archived contacts (including null)
      query = query.or('archived.is.null,archived.eq.false');
    }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }

      // Now fetch user roles separately for profiles that have user_id
      const profilesWithUserIds = (data || []).filter(profile => profile.user_id);
      let userRolesMap = new Map();

      if (profilesWithUserIds.length > 0) {
        const userIds = profilesWithUserIds.map(p => p.user_id);
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select(`
            user_id,
            role,
            organization_id,
            organizations!user_roles_organization_id_fkey(name)
          `)
          .in('user_id', userIds);

        if (userRoles) {
          userRoles.forEach(role => {
            userRolesMap.set(role.user_id, role);
          });
        }
      }

      // Batch query invitations for all contact emails to determine onboarding status
      const contactEmails = (data || [])
        .map(p => p.email)
        .filter(Boolean);

      const invitationMap = new Map<string, 'pending' | 'expired'>();
      
      if (contactEmails.length > 0) {
        const { data: invitations } = await supabase
          .from('user_invitations')
          .select('email, status, expires_at')
          .in('email', contactEmails)
          .eq('status', 'pending');

        invitations?.forEach(inv => {
          const isExpired = new Date(inv.expires_at) <= new Date();
          invitationMap.set(inv.email, isExpired ? 'expired' : 'pending');
        });
      }

      // Transform profiles data and include user role information
      const contacts = (data || []).map((profile: any) => {
        const userRole = profile.user_id ? userRolesMap.get(profile.user_id) : null;
        
        // Compute onboarding status
        let onboarding_status: OnboardingStatus;
        if (profile.user_id) {
          onboarding_status = 'onboarded';
        } else if (invitationMap.get(profile.email) === 'pending') {
          onboarding_status = 'invited_pending';
        } else if (invitationMap.get(profile.email) === 'expired') {
          onboarding_status = 'invited_expired';
        } else {
          onboarding_status = 'not_invited';
        }
        
        return {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          phone: profile.phone,
          job_title: profile.job_title,
          country: profile.country,
          contact_type: profile.contact_type || 'Buyer',
          relationship_status: profile.relationship_status || 'Prospect',
          customer_since: profile.customer_since,
          status: profile.status || 'Active',
          user_status: profile.user_status || 'no_account',
          profile_image: profile.profile_image,
          organization_id: profile.organization_id,
          internal_owner_id: profile.internal_owner_id,
          external_owner_id: profile.external_owner_id,
          notes: profile.notes,
          intended_system_role: profile.intended_system_role,
          created_at: profile.created_at,
          updated_at: profile.updated_at,
          username: profile.username,
          // Archive fields
          archived: profile.archived || false,
          archived_at: profile.archived_at,
          archived_reason: profile.archived_reason,
          // Onboarding status
          onboarding_status,
          // Last seen
          last_seen_at: profile.last_seen_at,
          // Partner ownership approval
          partner_ownership_status: profile.partner_ownership_status || null,
          organizations: profile.organizations ? {
            id: profile.organizations.id,
            name: profile.organizations.name,
            company_id: profile.organizations.company_id,
            relationship_status: profile.organizations.relationship_status
          } : undefined,
          internal_owner: profile.internal_owner,
          external_owner: profile.external_owner,
          has_user_account: !!profile.user_id, // Has account only if linked to authenticated user
          linked_user: profile.user_id ? {
            id: profile.user_id,
            username: profile.username || profile.email,
            email: profile.email,
            organization_name: userRole?.organizations?.name,
            role: userRole?.role
          } : null
        };
      });

      console.log("Fetched contacts with role information:", contacts.length);
      return contacts as Contact[];
    }
  });
};

export const useCreateContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateContactData) => {
      console.log("🔍 Creating contact with data:", data);
      console.log("🔍 Raw user_status from form:", data.user_status);
      console.log("🔍 Country field value:", data.country);
      console.log("🔍 Country field type:", typeof data.country);
      
      // Clean the data to convert "none" values to null/undefined, but preserve valid country values
      const cleanedData = {
        ...data,
        organization_id: data.organization_id === "none" ? null : data.organization_id,
        internal_owner_id: data.internal_owner_id === "none" ? null : data.internal_owner_id,
        external_owner_id: data.external_owner_id === "none" ? null : data.external_owner_id,
        // Only convert empty strings to null for country, preserve all other values
        country: data.country === "" ? null : data.country,
      };
      
      console.log("🔍 Cleaned data country:", cleanedData.country);
      console.log("🔍 Cleaned data user_status:", cleanedData.user_status);
      console.log("🔍 Will set user_status to:", cleanedData.user_status || 'no_account');
      
      // Check if profile with this email already exists.
      // The profiles table can legitimately contain multiple rows per email
      // (auth-linked profile + CRM stub rows), so .maybeSingle() would throw
      // PGRST116. Fetch up to 5 and pick the best candidate deterministically.
      const { data: existingProfiles, error: lookupError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, username, user_id')
        .eq('email', cleanedData.email)
        .limit(5);

      if (lookupError) {
        console.error("Lookup Error:", lookupError);
        throw new Error(lookupError.message || "Failed to check for existing contact");
      }

      const existingProfile =
        existingProfiles?.find(p => p.first_name && p.last_name) ??
        existingProfiles?.find(p => p.user_id) ??
        existingProfiles?.[0] ??
        null;

      let profileResult;

      if (existingProfile) {
        if (existingProfile.first_name && existingProfile.last_name) {
          throw new Error('A contact with this email already exists');
        }
        
        // Update existing profile with CRM data
        const updateData: any = {
          first_name: cleanedData.first_name,
          last_name: cleanedData.last_name,
          phone: cleanedData.phone,
          job_title: cleanedData.job_title,
          country: cleanedData.country,
          contact_type: cleanedData.contact_type,
          relationship_status: cleanedData.relationship_status || 'Prospect',
          customer_since: cleanedData.customer_since,
          status: cleanedData.status || 'Active',
          user_status: existingProfile.user_id ? 'active' : (cleanedData.user_status || 'no_account'),
          organization_id: cleanedData.organization_id,
          internal_owner_id: cleanedData.internal_owner_id,
          external_owner_id: cleanedData.external_owner_id,
          notes: cleanedData.notes,
          profile_image: cleanedData.profile_image,
          ...(cleanedData.partner_ownership_status ? { partner_ownership_status: cleanedData.partner_ownership_status } : {}),
        };

        // Store intended user role for when they sign up
        if (cleanedData.intended_system_role && cleanedData.organization_id && !existingProfile.user_id) {
          updateData.intended_system_role = cleanedData.intended_system_role;
        }

        console.log("🔍 Updating existing profile with user_status:", updateData.user_status);
        console.log("🔍 Updating existing profile with country:", updateData.country);
        
        const { data: result, error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', existingProfile.id)
          .select()
          .single();

        if (error) {
          console.error("Update Error:", error);
          throw new Error(error.message || "Failed to update contact");
        }

        profileResult = result;
      } else {
        // Create a new standalone contact profile with a generated UUID
        const insertData: any = {
          id: crypto.randomUUID(),
          first_name: cleanedData.first_name,
          last_name: cleanedData.last_name,
          email: cleanedData.email,
          phone: cleanedData.phone,
          job_title: cleanedData.job_title,
          country: cleanedData.country,
          contact_type: cleanedData.contact_type,
          relationship_status: cleanedData.relationship_status || 'Prospect',
          customer_since: cleanedData.customer_since,
          status: cleanedData.status || 'Active',
          user_status: cleanedData.user_status || 'no_account',
          organization_id: cleanedData.organization_id,
          internal_owner_id: cleanedData.internal_owner_id,
          external_owner_id: cleanedData.external_owner_id,
          notes: cleanedData.notes,
          profile_image: cleanedData.profile_image,
          ...(cleanedData.partner_ownership_status ? { partner_ownership_status: cleanedData.partner_ownership_status } : {}),
        };

        // Store intended user role for when they sign up
        if (cleanedData.intended_system_role && cleanedData.organization_id) {
          insertData.intended_system_role = cleanedData.intended_system_role;
        }

        console.log("🔍 Creating new profile with user_status:", insertData.user_status);
        console.log("🔍 Creating new profile with country:", insertData.country);
        console.log("🔍 Insert data:", insertData);

        const { data: result, error } = await supabase
          .from('profiles')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error("Insert Error:", error);
          throw new Error(error.message || "Failed to create contact");
        }

        console.log("🔍 Created profile result user_status:", result.user_status);
        console.log("🔍 Created profile result country:", result.country);
        profileResult = result;
      }

      // If user role is specified and organization is set, create a pending role assignment
      if (cleanedData.intended_system_role && cleanedData.organization_id && existingProfile?.user_id) {
        try {
          const { error: roleError } = await supabase
            .from('user_roles')
            .upsert({
              user_id: existingProfile.user_id,
              organization_id: cleanedData.organization_id,
              role: cleanedData.intended_system_role
            });

          if (roleError) {
            console.error("Role assignment error:", roleError);
            // Don't throw here as the contact was created successfully
            toast({
              title: "Warning",
              description: "Contact created but role assignment failed",
              variant: "destructive",
            });
          }
        } catch (roleError) {
          console.error("Role assignment error:", roleError);
        }
      }

      console.log("Contact created successfully:", profileResult);
      return profileResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
    },
    onError: (error) => {
      console.error("Create contact error:", error);
      toast({
        title: "Error",
        description: `Failed to create contact: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateContactData> }) => {
      const { data: result, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error("Update Error:", error);
        throw new Error(error.message || "Failed to update contact");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["pending-partner-contacts-count"] });
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update contact: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // Check if this is a user account
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', id)
        .single();

      if (profile?.username) {
        // For user accounts, just clear CRM data but keep the profile
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: null,
            last_name: null,
            phone: null,
            job_title: null,
            country: null,
            contact_type: null,
            relationship_status: null,
            customer_since: null,
            status: null,
            organization_id: null,
            internal_owner_id: null,
            external_owner_id: null,
            notes: null,
            profile_image: null
          })
          .eq('id', id);

        if (error) {
          throw new Error(error.message || "Failed to remove contact data");
        }
      } else {
        // For non-user contacts, delete the entire profile
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', id);

        if (error) {
          throw new Error(error.message || "Failed to delete contact");
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
      toast({
        title: "Success",
        description: "Contact removed successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to remove contact: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useSearchUsers = () => {
  return useMutation({
    mutationFn: async (searchTerm: string) => {
      console.log("Searching for users with term:", searchTerm);
      
      if (!searchTerm || searchTerm.length < 2) {
        console.log("Search term too short or empty");
        return [];
      }
      
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          organization_id,
          organizations!inner (
            id,
            name
          ),
          profiles!inner (
            id,
            username,
            email,
            first_name,
            last_name
          )
        `)
        .or(`profiles.username.ilike.%${searchTerm}%, profiles.email.ilike.%${searchTerm}%`)
        .not('profiles.username', 'is', null)
        .limit(10);

      if (error) {
        console.error("Search error:", error);
        throw error;
      }
      
      const transformedData = (data || []).map((item: any) => ({
        id: item.profiles.id,
        username: item.profiles.username || item.profiles.email,
        email: item.profiles.email,
        first_name: item.profiles.first_name,
        last_name: item.profiles.last_name,
        role: item.role,
        organization_id: item.organization_id,
        organization_name: item.organizations?.name || 'Unknown Organization',
        has_crm_data: !!(item.profiles.first_name && item.profiles.last_name)
      }));
      
      console.log("Enhanced search results with organization context:", transformedData);
      return transformedData;
    }
  });
};

export const useLinkUserToContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ contactId, userId }: { contactId: string; userId: string }) => {
      console.log("Linking user to contact:", { contactId, userId });
      
      const { data: contactData, error: contactError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', contactId)
        .single();

      if (contactError) {
        console.error("Error fetching contact:", contactError);
        throw contactError;
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw profileError;
      }

      if (profileData.first_name && profileData.last_name) {
        throw new Error('User profile already has contact information');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: contactData.first_name,
          last_name: contactData.last_name,
          phone: contactData.phone,
          job_title: contactData.job_title,
          country: contactData.country,
          contact_type: contactData.contact_type,
          relationship_status: contactData.relationship_status || 'Prospect',
          customer_since: contactData.customer_since,
          status: contactData.status,
          user_status: 'active', // Only set to active when actually linking an authenticated user
          organization_id: contactData.organization_id,
          internal_owner_id: contactData.internal_owner_id,
          external_owner_id: contactData.external_owner_id,
          notes: contactData.notes,
          profile_image: contactData.profile_image
        })
        .eq('id', userId);

      if (updateError) {
        console.error("Error updating profile:", updateError);
        throw updateError;
      }

      if (contactData.organization_id) {
        const { error: roleUpdateError } = await supabase
          .from('user_roles')
          .update({ organization_id: contactData.organization_id })
          .eq('user_id', userId);

        if (roleUpdateError) {
          console.error("Error updating user organization:", roleUpdateError);
        }
      }

      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', contactId);

      if (deleteError) {
        console.error("Error removing contact profile:", deleteError);
      }

      console.log("Successfully linked user to contact");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({
        title: "Success",
        description: "User successfully linked to contact",
      });
    },
    onError: (error) => {
      console.error("Link user error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });
};

export const useUnlinkUserFromContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (profileId: string) => {
      console.log("Unlinking user from contact:", profileId);
      
      const { data: profileData, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (fetchError) {
        console.error("Error fetching profile:", fetchError);
        throw fetchError;
      }

      if (profileData.first_name && profileData.last_name) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: crypto.randomUUID(),
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
            phone: profileData.phone,
            job_title: profileData.job_title,
            country: profileData.country,
            contact_type: profileData.contact_type,
            relationship_status: profileData.relationship_status || 'Prospect',
            customer_since: profileData.customer_since,
            status: profileData.status,
            user_status: 'no_account',
            organization_id: profileData.organization_id,
            internal_owner_id: profileData.internal_owner_id,
            external_owner_id: profileData.external_owner_id,
            notes: profileData.notes,
            profile_image: profileData.profile_image
          });

        if (insertError) {
          console.error("Error creating unlinked contact:", insertError);
          throw insertError;
        }
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: null,
          last_name: null,
          phone: null,
          job_title: null,
          country: null,
          contact_type: null,
          relationship_status: null,
          customer_since: null,
          status: null,
          organization_id: null,
          internal_owner_id: null,
          external_owner_id: null,
          notes: null,
          profile_image: null
        })
        .eq('id', profileId);

      if (updateError) {
        console.error("Error updating profile:", updateError);
        throw updateError;
      }

      console.log("Successfully unlinked user from contact");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({
        title: "Success",
        description: "User unlinked from contact",
      });
    },
    onError: (error) => {
      console.error("Unlink user error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });
};

export const useContactDeals = (contactId: string) => {
  return useQuery({
    queryKey: ["contact-deals", contactId],
    queryFn: async () => {
      console.log("Fetching deals for contact:", contactId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color)
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching contact deals:", error);
        throw error;
      }
      
      console.log("Fetched contact deals:", data);
      return data || [];
    },
    enabled: !!contactId,
  });
};
